import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchCategories } from "../../RTK/categories/GetCategories";
import { fetchBanners } from "../../RTK/categories/BannerSlice";
import CategoryPopup from "../../main-page/categories/category_popups/CategoryPopup ";
import UpdateCategoryPopup from "../../main-page/categories/category_popups/UpdateCategoryPopup";
import AddCategoryPopup from "./category_popups/AddCategoryPopup ";
import CategoryForm from "./category_popups/CategoryForm ";
import { toast } from "react-toastify"; // ✅ إضافة
import { usePermissions } from "../../contexts/PermissionsContext"; // ✅ إضافة

import {
  PlusCircle,
  Search,
  Layers,
  Package,
  BoxIcon,
  Eye,
  EyeOff,
  Loader2,
  RefreshCcw,
  Settings,
  ImageIcon,
  Lock, // ✅ إضافة
} from "lucide-react";
import BannerPopup from "./BannerPopup/BannerDetails";

const Categories = () => {
  const { hasPermission } = usePermissions(); // ✅ إضافة
  
  // ✅ فحص الصلاحية
  const canManageBanners = hasPermission("manage_banners");

  const [searchQuery, setSearchQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedCategoryType, setSelectedCategoryType] = useState("quantity");
  const [selectedItem, setSelectedItem] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isUpdatePopupOpen, setIsUpdatePopupOpen] = useState(false);
  const [isAddCategoryPopupOpen, setIsAddCategoryPopupOpen] = useState(false);
  const [isCategoryFormOpen, setIsCategoryFormOpen] = useState(false);

  const categoryTypes = [
    { id: "quantity", label: "الكمية", icon: <BoxIcon className="w-4 h-4" /> },
    { id: "package", label: "الباقات", icon: <Package className="w-4 h-4" /> },
    { id: "stock", label: "المخزون", icon: <Layers className="w-4 h-4" /> },
    { id: "banner", label: "البانر", icon: <ImageIcon className="w-4 h-4" /> }
  ];

  const categoryTypeValues = ["quantity", "package", "stock", "banner"];

  const dispatch = useDispatch();
  const categories = useSelector((state) => state.categories);
  const banners = useSelector((state) => state.banners);

  const isLoading = categories.isLoading || banners.isLoading;
  const error = categories.error || banners.error;

  useEffect(() => {
    if (selectedCategoryType === "banner") {
      dispatch(fetchBanners());
    } else {
      dispatch(fetchCategories(selectedCategoryType));
    }
  }, [selectedCategoryType, dispatch]);

  // ✅ حماية تغيير نوع الفئة للبانرات
  const handleCategoryTypeChange = (index) => {
    const newType = categoryTypeValues[index];
    
    // ✅ فحص الصلاحية للبانرات
    if (newType === "banner" && !canManageBanners) {
      toast.error("ليس لديك صلاحية لعرض البانرات 🔒");
      return;
    }

    setActiveIndex(index);
    setSelectedCategoryType(newType);
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const getFilteredData = () => {
    const data = selectedCategoryType === "banner" ? banners.data : categories.data;
    return data.filter(item => {
      const searchTerm = item.name || item.title || "";
      return searchTerm.toLowerCase().includes(searchQuery.toLowerCase());
    });
  };

  // ✅ حماية فتح تفاصيل البانر
  const openPopup = (item) => {
    if (selectedCategoryType === "banner" && !canManageBanners) {
      toast.error("ليس لديك صلاحية لعرض تفاصيل البانرات 🔒");
      return;
    }

    setSelectedItem(item);
    setIsPopupOpen(true);
  };

  const closePopup = () => {
    setIsPopupOpen(false);
    setSelectedItem(null);
  };

  // ✅ حماية فتح نافذة التعديل
  const openUpdatePopup = () => {
    if (selectedCategoryType === "banner" && !canManageBanners) {
      toast.error("ليس لديك صلاحية لتعديل البانرات 🔒");
      return;
    }

    setIsUpdatePopupOpen(true);
    setIsPopupOpen(false);
  };

  const closeUpdatePopup = () => {
    setIsUpdatePopupOpen(false);
  };

  // ✅ حماية زر الإضافة
  const handleAddClick = () => {
    if (selectedCategoryType === "banner" && !canManageBanners) {
      toast.error("ليس لديك صلاحية لإضافة بانرات جديدة 🔒");
      return;
    }

    setIsAddCategoryPopupOpen(true);
  };

  const closeAddPopup = () => {
    setIsAddCategoryPopupOpen(false);
  };

  const handleCategoryTypeSelect = (type) => {
    setSelectedCategoryType(type);
    setIsAddCategoryPopupOpen(false);
    setIsCategoryFormOpen(true);
  };

  const closeCategoryForm = () => {
    setIsCategoryFormOpen(false);
  };

  const handleRetry = () => {
    if (selectedCategoryType === "banner") {
      dispatch(fetchBanners());
    } else {
      dispatch(fetchCategories(selectedCategoryType));
    }
  };

  return (
    <div className="min-h-screen p-6 bg-gray-900" dir="rtl">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col lg:flex-row gap-4 justify-between items-center bg-gray-800/50 backdrop-blur supports-[backdrop-filter]:bg-gray-800/50 p-4 rounded-xl shadow-lg border border-gray-800/50">
          {/* ✅ زر الإضافة - محمي للبانرات */}
          {selectedCategoryType === "banner" && !canManageBanners ? (
            <div className="flex items-center gap-2 px-6 py-2.5 bg-slate-800/50 rounded-lg border border-slate-700">
              <Lock className="w-5 h-5 text-slate-500" />
              <span className="text-slate-400 font-semibold">
                🔒 ليس لديك صلاحية إضافة بانرات
              </span>
            </div>
          ) : (
            <button
              onClick={handleAddClick}
              className="group flex items-center gap-2 px-6 py-2.5 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              <PlusCircle className="w-5 h-5 transition-transform group-hover:rotate-90" />
              <span>إضافة {selectedCategoryType === "banner" ? "بانر" : "تصنيف"}</span>
            </button>
          )}

          {/* Category Type Selector */}
          <div className="flex p-1 bg-gray-700/50 rounded-lg">
            {categoryTypes.map((type, index) => (
              <button
                key={type.id}
                onClick={() => handleCategoryTypeChange(index)}
                disabled={type.id === "banner" && !canManageBanners}
                className={`
                  flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition-all duration-300
                  ${selectedCategoryType === type.id
                    ? "bg-yellow-500 text-black shadow-md" 
                    : "text-gray-300 hover:bg-gray-600/50"}
                  ${type.id === "banner" && !canManageBanners
                    ? "opacity-50 cursor-not-allowed"
                    : ""}
                `}
              >
                {type.icon}
                <span>{type.label}</span>
                {/* ✅ أيقونة قفل على تاب البانر */}
                {type.id === "banner" && !canManageBanners && (
                  <Lock className="w-3 h-3 ml-1" />
                )}
              </button>
            ))}
          </div>

          {/* Search Bar */}
          <div className="relative w-full lg:w-72">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder={`بحث في ${selectedCategoryType === "banner" ? "البانرات" : "التصنيفات"}...`}
              value={searchQuery}
              onChange={handleSearch}
              className="w-full bg-gray-700/50 text-white pr-10 pl-4 py-2.5 rounded-lg border border-gray-600/50 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 transition-colors placeholder-gray-400"
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div>
        {isLoading ? (
          <div className="flex flex-col items-center justify-center min-h-[400px] gap-3">
            <Loader2 className="w-8 h-8 text-yellow-500 animate-spin" />
            <span className="text-gray-400">جاري التحميل...</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
            <p className="text-red-500 text-lg">{error}</p>
            <button 
              onClick={handleRetry}
              className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              <RefreshCcw className="w-4 h-4" />
              <span>إعادة المحاولة</span>
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {getFilteredData().map((item) => (
              <div
                key={item.id}
                onClick={() => openPopup(item)}
                className={`group relative overflow-hidden rounded-xl bg-gray-800/50 ${
                  selectedCategoryType === "banner" && !canManageBanners
                    ? "cursor-not-allowed opacity-70"
                    : "cursor-pointer"
                }`}
              >
                {/* ✅ أيقونة قفل على كروت البانرات */}
                {selectedCategoryType === "banner" && !canManageBanners && (
                  <div className="absolute top-2 left-2 bg-slate-700/90 p-1.5 rounded-lg z-10">
                    <Lock className="w-3 h-3 text-slate-400" />
                  </div>
                )}

                <div
                  className={`aspect-video w-full bg-cover bg-center transition-transform duration-500 group-hover:scale-110 ${
                    (item.is_hidden || !item.is_active) ? 'opacity-40' : 'opacity-100'
                  }`}
                  style={{
                    backgroundImage: `url(${
                      item.image ||
                      item.product_image ||
                      item.banner_image
                    })`,
                  }}
                />

                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-80 group-hover:opacity-90 transition-opacity" />

                <div className="absolute inset-0 p-4 flex flex-col justify-end transform transition-transform duration-300">
                  <h3 className="text-white text-lg text-left font-bold group-hover:scale-105 transition-transform">
                    {item.name || item.title}
                  </h3>
                  
                  {selectedCategoryType === "banner" && (
                    <p className="text-gray-300 text-sm mt-2">{item.description}</p>
                  )}

                  <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-black/50 backdrop-blur px-2 py-1 rounded-lg">
                    {(item.is_hidden || !item.is_active) ? (
                      <>
                        <EyeOff className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-400">مخفي</span>
                      </>
                    ) : (
                      <>
                        <Eye className="w-4 h-4 text-white" />
                        <span className="text-sm text-white">ظاهر</span>
                      </>
                    )}
                  </div>

                  {/* ✅ زر التعديل - محمي للبانرات */}
                  {!(selectedCategoryType === "banner" && !canManageBanners) && (
                    <button 
                      className="absolute top-3 left-3 p-2 bg-black/50 backdrop-blur rounded-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
                      onClick={(e) => {
                        e.stopPropagation();
                        openUpdatePopup();
                      }}
                    >
                      <Settings className="w-4 h-4 text-white" />
                    </button>
                  )}
                </div>

                <div className="absolute inset-0 border-2 border-yellow-500/0 group-hover:border-yellow-500/100 rounded-xl transition-colors duration-300" />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Popups */}
      {selectedCategoryType === "banner" ? (
        canManageBanners && (
          <BannerPopup
            isVisible={isPopupOpen}
            bannerId={selectedItem?.id}
            onClose={closePopup}
          />
        )
      ) : (
        <>
          <CategoryPopup
            isVisible={isPopupOpen}
            category={selectedItem}
            categoryType={selectedCategoryType}
            onClose={closePopup}
            onUpdateClick={openUpdatePopup}
          />

          {isUpdatePopupOpen && (
            <UpdateCategoryPopup
              category={selectedItem}
              categoryType={selectedCategoryType}
              isVisible={isUpdatePopupOpen}
              onClose={closeUpdatePopup}
            />
          )}
        </>
      )}

      {isAddCategoryPopupOpen && (
        selectedCategoryType === "banner" ? (
          canManageBanners && (
            <AddCategoryPopup
              isVisible={isAddCategoryPopupOpen}
              onClose={closeAddPopup}
              onCategoryTypeSelect={handleCategoryTypeSelect}
              isBannerMode={selectedCategoryType === "banner"}
            />
          )
        ) : (
          <AddCategoryPopup
            isVisible={isAddCategoryPopupOpen}
            onClose={closeAddPopup}
            onCategoryTypeSelect={handleCategoryTypeSelect}
            isBannerMode={selectedCategoryType === "banner"}
          />
        )
      )}

      {isCategoryFormOpen && (
        selectedCategoryType === "banner" ? (
          canManageBanners && (
            <CategoryForm
              categoryType={selectedCategoryType}
              onClose={closeCategoryForm}
              isBannerMode={selectedCategoryType === "banner"}
            />
          )
        ) : (
          <CategoryForm
            categoryType={selectedCategoryType}
            onClose={closeCategoryForm}
            isBannerMode={selectedCategoryType === "banner"}
          />
        )
      )}
    </div>
  );
};

export default Categories;